// H2 Farm calculations - pure functions

import { CAPEX_UNIT, OPTIMIZER_SURFACES, OPTIMIZER_VOLUMES, PROD_DAYS_PER_YEAR } from "./defaults";
import { aggregateChannels } from "./pricing";

// PHASE 2 · B.4 — Saffron commercial engine.
// If `s.commercial.channels[]` is present, delegate to the unified pricing engine.
// Otherwise fall back to legacy scalar fields (mix_*, yield_*, price_*).
// Output shape is preserved (raw_*, kg_*, rev_*, total) so that all downstream
// consumers (OverviewTab, PrintReport, calcSummary.coreRevenue, runOptimizer)
// keep working without modification.
export const calcSaffron = (s, opts = {}) => {
  const currentYear = Number(opts.current_year) || 1;

  // Legacy path (compat) — when commercial block is missing
  if (!s?.commercial?.channels || !Array.isArray(s.commercial.channels) || s.commercial.channels.length === 0) {
    const total = Math.max(0, s.mix_dried + s.mix_extract + s.mix_scco2);
    const norm = total > 0 ? total : 1;
    const raw_dried = (s.kg_year * s.mix_dried) / norm;
    const raw_extract = (s.kg_year * s.mix_extract) / norm;
    const raw_scco2 = (s.kg_year * s.mix_scco2) / norm;
    const kg_dried = raw_dried;
    const kg_extract = (raw_extract * s.yield_extract) / 100;
    const kg_scco2 = (raw_scco2 * s.yield_scco2) / 100;
    const rev_dried = kg_dried * s.price_dried;
    const rev_extract = kg_extract * s.price_extract;
    const rev_scco2 = kg_scco2 * s.price_scco2;
    return {
      raw_dried, raw_extract, raw_scco2,
      kg_dried, kg_extract, kg_scco2,
      rev_dried, rev_extract, rev_scco2,
      total: rev_dried + rev_extract + rev_scco2,
      source: "legacy",
    };
  }

  // Canonical commercial path (Phase 2 · B.4)
  const scenario = s.commercial.active_scenario || "base";
  const agg = aggregateChannels(s.commercial.channels, s.kg_year, scenario, currentYear);

  // Map channels back to the legacy output field names for compat.
  const byKey = (k) => agg.channels.find((c) => c.key === k) || {};
  const dried = byKey("dried");
  const ext = byKey("extract_std");
  const scco2 = byKey("extract_scco2");

  return {
    raw_dried: dried.raw_allocated || 0,
    raw_extract: ext.raw_allocated || 0,
    raw_scco2: scco2.raw_allocated || 0,
    kg_dried: dried.final_kg || 0,
    kg_extract: ext.final_kg || 0,
    kg_scco2: scco2.final_kg || 0,
    rev_dried: dried.revenue || 0,
    rev_extract: ext.revenue || 0,
    rev_scco2: scco2.revenue || 0,
    total: agg.total_revenue,
    // Additional fields (do not break legacy consumers, only extend)
    commercial: {
      scenario: agg.scenario,
      channels: agg.channels,
      allocation_sum_pct: agg.allocation_sum_pct,
      conservation_of_mass_ok: agg.conservation_of_mass_ok,
      weighted_avg_price: agg.weighted_avg_price,
    },
    source: "commercial",
  };
};

export const calcAlgaeSpecies = (sp) => {
  const kg_dried = (sp.kg_year * sp.yield_dried) / 100;
  const kg_scco2 = (sp.kg_year * sp.yield_scco2) / 100;
  const rev_dried = kg_dried * sp.price_dried;
  const rev_scco2 = kg_scco2 * sp.price_scco2;
  return { name: sp.name, kg_dried, kg_scco2, rev_dried, rev_scco2, total: rev_dried + rev_scco2 };
};

export const calcAlgae = (algae) => {
  const chlorella = calcAlgaeSpecies(algae.chlorella);
  const spirulina = calcAlgaeSpecies(algae.spirulina);
  const scenedesmus = calcAlgaeSpecies(algae.scenedesmus);
  return {
    chlorella, spirulina, scenedesmus,
    total: chlorella.total + spirulina.total + scenedesmus.total,
    total_biomass: algae.chlorella.kg_year + algae.spirulina.kg_year + algae.scenedesmus.kg_year,
  };
};

export const calcOpex = (opex) => {
  const monthly = opex.energy + opex.consumables + opex.labor + opex.maintenance;
  return { monthly, annual: monthly * 12 };
};

export const calcFutureLots = (lots = []) => {
  const enabled = lots.filter((l) => l.enabled);
  const revenue = enabled.reduce((s, l) => s + (Number(l.annual_revenue) || 0), 0);
  const opex = enabled.reduce((s, l) => s + (Number(l.annual_opex) || 0), 0);
  const capex = enabled.reduce((s, l) => s + (Number(l.capex) || 0), 0);
  return { revenue, opex, capex, ebitda: revenue - opex, count: enabled.length };
};

// --- NEW LOTS (Lotto 3, 4, 5) governance-aware calculations ---
// activation_year controls when the lot enters the P&L in the 5Y projection.
// For the base "current year" summary we treat all enabled lots as active.

export const calcNewLot = (lot) => {
  if (!lot) return null;
  const scenario = lot.scenario || "base";
  const production_kg = lot.production_scenarios?.[scenario] || 0;
  const revenue = lot.revenue_scenarios?.[scenario] || 0;
  const opex = lot.opex_scenarios?.[scenario] || 0;
  const capex = lot.capex_scenarios?.base || 0;
  const ebitda = revenue - opex;
  const ebitda_margin = revenue > 0 ? (ebitda / revenue) * 100 : 0;
  const roi_operating_annual = capex > 0 ? (ebitda / capex) * 100 : 0;
  const payback_cashflow = ebitda > 0 ? capex / ebitda : null;

  // KPI per m²
  const revenue_per_m2 = lot.physical_area_m2 > 0 ? revenue / lot.physical_area_m2 : 0;
  const ebitda_per_m2 = lot.physical_area_m2 > 0 ? ebitda / lot.physical_area_m2 : 0;
  const ebitda_over_capex = capex > 0 ? ebitda / capex : 0;

  // Path allocation check (sum should be 100)
  const path_sum = (lot.commercialization_path || []).reduce((s, p) => s + (Number(p.pct) || 0), 0);
  const species_sum = (lot.species || []).reduce((s, sp) => s + (Number(sp.allocation_pct) || 0), 0);

  // Governance warnings
  const warnings = [];
  const bounds = lot.hard_bounds_kg || {};
  if (production_kg > 0 && bounds.critical_over && production_kg > bounds.critical_over) {
    warnings.push({ level: "CRITICAL", msg: `Produzione ${production_kg} kg > critical_over ${bounds.critical_over}` });
  } else if (production_kg > 0 && bounds.warn_over && production_kg > bounds.warn_over) {
    warnings.push({ level: "WARNING", msg: `Produzione ${production_kg} kg > warn_over ${bounds.warn_over}` });
  }
  if (bounds.max && production_kg > bounds.max * 1.2) {
    warnings.push({ level: "WARNING", msg: `Produzione > MAX × 1.20 (${bounds.max * 1.2} kg)` });
  }
  if (bounds.min && production_kg > 0 && production_kg < bounds.min * 0.8) {
    warnings.push({ level: "WARNING", msg: `Produzione < MIN × 0.80 (${bounds.min * 0.8} kg)` });
  }
  if (ebitda_margin > 85) warnings.push({ level: "CRITICAL", msg: `EBITDA margin ${ebitda_margin.toFixed(1)}% > 85%` });
  else if (ebitda_margin > 70) warnings.push({ level: "WARNING", msg: `EBITDA margin ${ebitda_margin.toFixed(1)}% > 70%` });
  if (payback_cashflow !== null && payback_cashflow < 0.5) warnings.push({ level: "CRITICAL", msg: `Payback < 6 mesi` });
  else if (payback_cashflow !== null && payback_cashflow < 1) warnings.push({ level: "WARNING", msg: `Payback < 1 anno` });
  if (Math.abs(path_sum - 100) > 0.5) warnings.push({ level: "WARNING", msg: `Commercialization path sum = ${path_sum}% (deve essere 100%)` });
  if (Math.abs(species_sum - 100) > 0.5) warnings.push({ level: "WARNING", msg: `Species allocation sum = ${species_sum}% (deve essere 100%)` });

  // Lot5 plants sanity
  if (lot.id === "lot5" && lot.total_plants) {
    const expected = (lot.physical_area_m2 || 0) * (lot.vertical_levels || 0) * (lot.plants_per_m2_per_level || 0);
    if (Math.abs(expected - lot.total_plants) > 1) {
      warnings.push({ level: "WARNING", msg: `total_plants ${lot.total_plants} ≠ ${expected} (surface × levels × density)` });
    }
  }

  return {
    id: lot.id,
    scenario,
    production_kg,
    revenue,
    opex,
    ebitda,
    ebitda_margin,
    capex,
    roi_operating_annual,
    payback_cashflow,
    revenue_per_m2,
    ebitda_per_m2,
    ebitda_over_capex,
    energy_kwh_year: lot.energy_kwh_year || 0,
    water_m3_year: lot.water_m3_year || 0,
    physical_area_m2: lot.physical_area_m2 || 0,
    activation_year: lot.activation_year || 1,
    path_sum,
    species_sum,
    warnings,
  };
};

export const calcNewLots = (newLots = {}) => {
  const keys = Object.keys(newLots);
  const items = keys.map((k) => ({ key: k, lot: newLots[k], calc: calcNewLot(newLots[k]) }));
  const enabled = items.filter((x) => x.lot.enabled);
  const total = {
    revenue: enabled.reduce((s, x) => s + x.calc.revenue, 0),
    opex: enabled.reduce((s, x) => s + x.calc.opex, 0),
    ebitda: enabled.reduce((s, x) => s + x.calc.ebitda, 0),
    capex: enabled.reduce((s, x) => s + x.calc.capex, 0),
    physical_area_m2: enabled.reduce((s, x) => s + x.calc.physical_area_m2, 0),
    energy_kwh_year: enabled.reduce((s, x) => s + x.calc.energy_kwh_year, 0),
    water_m3_year: enabled.reduce((s, x) => s + x.calc.water_m3_year, 0),
  };
  total.ebitda_margin = total.revenue > 0 ? (total.ebitda / total.revenue) * 100 : 0;
  return { items, total, count: enabled.length };
};

// Expansion optimizer: rank the 3 new lots by attractiveness metrics
export const rankNewLotsExpansion = (newLotsCalc) => {
  const items = (newLotsCalc.items || []).map((x) => ({
    ...x,
    score:
      (x.calc.ebitda_per_m2 || 0) * 0.35 +
      (x.calc.ebitda_over_capex || 0) * 100 * 0.30 +
      (x.calc.revenue_per_m2 || 0) * 0.20 +
      (x.calc.payback_cashflow ? (10 / x.calc.payback_cashflow) : 0) * 0.15,
  }));
  return items.sort((a, b) => b.score - a.score);
};

// Model audit — returns list of check results
export const auditModel = (inputs, summary) => {
  const checks = [];
  const addCheck = (level, category, msg, current, expected, formula) => {
    checks.push({ level, category, msg, current, expected, formula });
  };

  // Physical area check for Lotto 4 (dimensional check)
  const l4 = inputs.new_lots?.lot4;
  if (l4) {
    const expectedEq = l4.physical_area_m2 * l4.vertical_levels;
    if (Math.abs(expectedEq - l4.equivalent_area_m2) > 1) {
      addCheck("YELLOW", "Dimensional", "Superficie equivalente Lotto 4 non corrisponde a fisica × livelli", `${l4.equivalent_area_m2} m²`, `${expectedEq} m²`, "physical × levels");
    }
    // Anti double-counting sanity check: kg_year should NOT scale with equivalent_area × 4
    const prod = l4.production_scenarios?.base || 0;
    if (prod > 20000) {
      addCheck("RED", "Double-count", `Lotto 4 produzione BASE ${prod} kg troppo alta — sospetto double-counting su livelli`, `${prod} kg`, "~9.600 kg base", "kg = physical × levels × yield_kg_m2_ciclo × cicli");
    }
  }

  // Lotto 5 plants check
  const l5 = inputs.new_lots?.lot5;
  if (l5) {
    const expected = (l5.physical_area_m2 || 0) * (l5.vertical_levels || 0) * (l5.plants_per_m2_per_level || 0);
    if (Math.abs(expected - (l5.total_plants || 0)) > 1) {
      addCheck("YELLOW", "Dimensional", `Lotto 5 total_plants non coerente`, `${l5.total_plants}`, `${expected}`, "area × levels × density");
    }
  }

  // Existing lots not touched
  addCheck("GREEN", "Immutability", "Lotti 1 e 2 non modificati (mix_* invariati)", "OK", "OK", "-");

  // Iterate new lots warnings
  Object.values(inputs.new_lots || {}).forEach((lot) => {
    if (!lot.enabled) return;
    const c = calcNewLot(lot);
    c.warnings.forEach((w) => {
      addCheck(w.level === "CRITICAL" ? "RED" : "YELLOW", `Sanity ${lot.name}`, w.msg, "-", "-", "-");
    });
  });

  // Revenue sanity
  const nl = calcNewLots(inputs.new_lots || {});
  const revLot3 = inputs.new_lots?.lot3?.enabled ? calcNewLot(inputs.new_lots.lot3).revenue : 0;
  const revLot4 = inputs.new_lots?.lot4?.enabled ? calcNewLot(inputs.new_lots.lot4).revenue : 0;
  const revLot5 = inputs.new_lots?.lot5?.enabled ? calcNewLot(inputs.new_lots.lot5).revenue : 0;
  if (revLot3 > 150000) addCheck("YELLOW", "Revenue anomaly", `Lotto 3 ricavi ${revLot3} > cap 150k`, `${revLot3}`, "≤ 150k", "-");
  if (revLot4 > 220000) addCheck("YELLOW", "Revenue anomaly", `Lotto 4 ricavi ${revLot4} > cap 220k`, `${revLot4}`, "≤ 220k", "-");
  if (revLot5 > 60000) addCheck("YELLOW", "Revenue anomaly", `Lotto 5 ricavi ${revLot5} > cap 60k`, `${revLot5}`, "≤ 60k", "-");

  if (nl.count > 0) {
    addCheck("GREEN", "Aggregation", `${nl.count} nuovi lotti attivi, ricavi complessivi ${Math.round(nl.total.revenue)} €`, `${Math.round(nl.total.revenue)}`, "220k-430k range", "sum(revenue by scenario)");
  }

  // Global sanity: EBITDA margin overall
  if (summary?.marginPct > 85) addCheck("RED", "EBITDA margin", `Margin ${summary.marginPct.toFixed(1)}% > 85%`, "-", "≤ 85%", "-");
  else if (summary?.marginPct > 70) addCheck("YELLOW", "EBITDA margin", `Margin ${summary.marginPct.toFixed(1)}% > 70%`, "-", "≤ 70%", "-");

  return checks;
};

// --- Energy module (v2) — subsystems, monthly dispatch, H2 & fuel cell ------
// Priority order per month:
//   1. FV → consumo diretto
//   2. FV surplus → batteria (fino a capacità disponibile)
//   3. FV surplus → elettrolizzatore (fino a capacità mensile)
//   4. FV surplus → rete/curtailment
// Inverno (Nov-Feb): H2 → fuel cell → elettricità + calore
export const WINTER_MONTHS = [10, 11, 0, 1]; // Nov, Dec, Jan, Feb (indexes)

const subsystemKwp = (s) => {
  if (!s || !s.enabled) return 0;
  // bacino: surface_m2 × coverage% × wp/m² / 1000
  if (s.coverage_pct !== undefined) {
    return ((s.surface_m2 || 0) * (s.coverage_pct / 100) * (s.wp_per_m2 || 0)) / 1000;
  }
  // terra: terrain × occupation% × wp/m² / 1000 + bifacial gain
  if (s.terrain_m2 !== undefined) {
    const base = ((s.terrain_m2 || 0) * ((s.occupation_pct || 0) / 100) * (s.wp_per_m2 || 0)) / 1000;
    return s.bifacial ? base * (1 + (s.bifacial_gain_pct || 0) / 100) : base;
  }
  // serra & tettoie: surface × wp / 1000
  return ((s.surface_m2 || 0) * (s.wp_per_m2 || 0)) / 1000;
};

const subsystemProduction = (s) => {
  const kwp = subsystemKwp(s);
  return kwp * (s.specific_prod || 0) * ((s.performance_ratio || 100) / 100);
};

const subsystemCapex = (s) => {
  const kwp = subsystemKwp(s);
  return kwp * (s.capex_eur_kwp || 0);
};

export const calcEnergy = (energy) => {
  if (!energy) return null;

  const subs = energy.subsystems || {};
  const subsEntries = Object.entries(subs).map(([key, s]) => ({
    key,
    ...s,
    kwp: subsystemKwp(s),
    production: subsystemProduction(s),
    capex: subsystemCapex(s),
  }));

  const totalKwp = subsEntries.reduce((sum, s) => sum + s.kwp, 0);
  const totalProduction = subsEntries.reduce((sum, s) => sum + s.production, 0);
  const pv_capex = subsEntries.reduce((sum, s) => sum + s.capex, 0);

  const loads = energy.load_categories || [];
  const totalConsumption = loads.reduce((sum, l) => sum + (Number(l.kwh_year) || 0), 0);

  // Monthly profiles (production per month = totalProduction × share)
  const monthlyProfile = energy.monthly_profile || Array(12).fill(1 / 12);
  const monthlyProduction = monthlyProfile.map((p) => totalProduction * p);
  const monthlyConsumption = Array(12).fill(totalConsumption / 12);

  // Battery
  const battery = energy.battery || { capacity_kwh: 0, round_trip_efficiency_pct: 90, dod_pct: 90 };
  const batteryUsableKwh = (battery.capacity_kwh || 0) * ((battery.dod_pct || 100) / 100);
  const rt_eff = (battery.round_trip_efficiency_pct || 100) / 100;

  // Electrolyzer
  const electro = energy.electrolyzer || {};
  const electroKwhCapacityYear = (electro.power_kw || 0) * (electro.operating_hours_year || 0);
  const electroKwhMonth = electroKwhCapacityYear / 12;

  // Fuel cell
  const fc = energy.fuel_cell || {};
  const fcKwhCapacityYear = (fc.power_kw || 0) * (fc.operating_hours_year || 0);
  // kWh H2 → kWh elettrici (LHV H2 = 33.3 kWh/kg)
  const H2_LHV_KWH_KG = 33.3;

  // Monthly dispatch simulation
  let batterySOC = 0; // kWh stored
  let h2Storage = 0; // kg H2 stored
  const storageCapKg = electro.storage_capacity_kg || 0;

  const monthly = [];
  let totalDirect = 0;
  let totalToBattery = 0;
  let totalFromBattery = 0;
  let totalToElectro = 0;
  let totalFromGrid = 0;
  let totalToGrid = 0;
  let totalCurtailment = 0;
  let totalH2Produced = 0;
  let totalH2ConsumedFC = 0;
  let totalFCElectric = 0;
  let totalFCHeat = 0;

  for (let m = 0; m < 12; m++) {
    const prod = monthlyProduction[m];
    const cons = monthlyConsumption[m];

    // 1. Autoconsumo diretto
    const direct = Math.min(prod, cons);
    let surplus = prod - direct;
    let deficit = cons - direct;

    // 2. FV → batteria (carica)
    const chargeSpace = batteryUsableKwh - batterySOC;
    const monthlyChargeLimit = batteryUsableKwh * 30; // ampia (30 cicli mese)
    const toBattery = Math.min(surplus, Math.max(0, chargeSpace), monthlyChargeLimit);
    surplus -= toBattery;
    batterySOC += toBattery;

    // 3. FV → elettrolizzatore
    const surplusShare = (electro.surplus_share_pct || 0) / 100;
    const toElectro = Math.min(surplus * surplusShare, electroKwhMonth);
    surplus -= toElectro;
    const h2ProducedM = electro.kwh_per_kg_h2 > 0 ? toElectro / electro.kwh_per_kg_h2 : 0;
    // Add to storage, respect cap
    const storageSpace = storageCapKg - h2Storage;
    const h2StoredM = Math.min(h2ProducedM, Math.max(0, storageSpace));
    const h2Curtailed = h2ProducedM - h2StoredM;
    h2Storage += h2StoredM;

    // 4. Batteria → deficit (scarica con round-trip efficiency)
    let fromBattery = 0;
    if (deficit > 0 && batterySOC > 0) {
      const discharge = Math.min(batterySOC, deficit / rt_eff);
      batterySOC -= discharge;
      fromBattery = discharge * rt_eff;
      deficit -= fromBattery;
    }

    // 5. Fuel cell (winter months only): H2 → elettricità + calore
    let fcElectric = 0;
    let fcHeat = 0;
    let h2ConsumedFC = 0;
    if (WINTER_MONTHS.includes(m) && deficit > 0 && h2Storage > 0 && fc.power_kw > 0) {
      // Elettricità che FC può produrre da H2 stoccato
      const kWh_electric_per_kg = H2_LHV_KWH_KG * ((fc.electric_efficiency_pct || 0) / 100);
      const kWh_capacity_month = fcKwhCapacityYear / 4; // concentrata su 4 mesi inverno
      const wanted_electric = Math.min(deficit, kWh_capacity_month);
      const kg_needed = kWh_electric_per_kg > 0 ? wanted_electric / kWh_electric_per_kg : 0;
      const kg_available = Math.min(h2Storage, kg_needed);
      h2ConsumedFC = kg_available;
      h2Storage -= h2ConsumedFC;
      fcElectric = h2ConsumedFC * kWh_electric_per_kg;
      // Calore recuperato: energia H2 non convertita in elettricità × heat_recovery_pct
      const totalH2Energy = h2ConsumedFC * H2_LHV_KWH_KG;
      fcHeat = (totalH2Energy - fcElectric) * ((fc.heat_recovery_pct || 0) / 100);
      deficit -= fcElectric;
    }

    // 6. Deficit residuo → rete
    const fromGrid = Math.max(0, deficit);

    // 7. Surplus finale → rete/curtailment
    const toGrid = surplus;

    monthly.push({
      month: m,
      production: prod,
      consumption: cons,
      direct,
      toBattery,
      fromBattery,
      toElectro,
      fromGrid,
      toGrid,
      h2Produced: h2ProducedM,
      h2Stored: h2StoredM,
      h2Curtailed,
      h2Consumed: h2ConsumedFC,
      h2StorageEnd: h2Storage,
      fcElectric,
      fcHeat,
      batterySOC,
    });

    totalDirect += direct;
    totalToBattery += toBattery;
    totalFromBattery += fromBattery;
    totalToElectro += toElectro;
    totalFromGrid += fromGrid;
    totalToGrid += toGrid;
    totalCurtailment += h2Curtailed * (electro.kwh_per_kg_h2 || 0);
    totalH2Produced += h2StoredM;
    totalH2ConsumedFC += h2ConsumedFC;
    totalFCElectric += fcElectric;
    totalFCHeat += fcHeat;
  }

  const coverageDirect = totalConsumption > 0 ? (totalDirect / totalConsumption) * 100 : 0;
  const autoconsumoFV = totalProduction > 0 ? ((totalDirect + totalToBattery + totalToElectro) / totalProduction) * 100 : 0;
  const autosufficienza = totalConsumption > 0
    ? ((totalDirect + totalFromBattery + totalFCElectric) / totalConsumption) * 100
    : 0;

  // Economics
  const energy_savings = (totalDirect + totalFromBattery + totalFCElectric) * (energy.grid_price_eur_kwh || 0);
  const grid_cost = totalFromGrid * (energy.grid_price_eur_kwh || 0);
  const feed_in_revenue = totalToGrid * (energy.grid_feed_price_eur_kwh || 0);
  const h2_revenue = totalH2Produced * (energy.h2_price_eur_kg || 0);
  const heat_recovered_mwh = totalFCHeat / 1000;

  // CAPEX
  const bess_capex = (battery.capacity_kwh || 0) * (battery.capex_eur_kwh || 0);
  const electro_capex = (electro.power_kw || 0) * (electro.capex_eur_kw || 0);
  const h2_storage_capex = (electro.storage_capacity_kg || 0) * (electro.storage_capex_eur_kg || 0);
  const fc_capex = (fc.power_kw || 0) * (fc.capex_eur_kw || 0);
  const energy_capex = pv_capex + bess_capex + electro_capex + h2_storage_capex + fc_capex;

  const annual_benefit = energy_savings + feed_in_revenue;
  const energy_payback_years = annual_benefit > 0 ? energy_capex / annual_benefit : null;
  const energy_payback_savings_only = energy_savings > 0 ? energy_capex / energy_savings : null;

  return {
    subsystems: subsEntries,
    totalKwp,
    totalProduction, // netProduction (già include PR)
    netProduction: totalProduction,
    grossProduction: totalProduction / 0.82, // stima gross
    totalConsumption,
    monthly,
    // Aggregati
    totalDirect,
    totalToBattery,
    totalFromBattery,
    totalToElectro,
    totalFromGrid,
    totalToGrid,
    totalCurtailment,
    totalH2Produced,
    totalH2ConsumedFC,
    totalFCElectric,
    totalFCHeat,
    heat_recovered_mwh,
    // KPI compat con vecchio calcolo
    selfConsumedFromPV: totalDirect + totalFromBattery,
    surplus: totalToElectro + totalToGrid,
    fromGrid: totalFromGrid,
    // Indicatori distinti
    coverageDirect,
    autoconsumoFV,
    autosufficienza,
    // Economics
    energy_savings,
    grid_cost,
    feed_in_revenue,
    h2_revenue,
    annual_energy_benefit: energy_savings,
    annual_energy_full_benefit: annual_benefit,
    // CAPEX
    pv_capex,
    bess_capex,
    electro_capex,
    h2_storage_capex,
    fc_capex,
    energy_capex,
    // Payback
    energy_payback_years: energy_payback_savings_only,
    energy_payback_full: energy_payback_years,
    // Alias legacy per compatibilità
    h2_kg_year: totalH2Produced,
    coverage_pct: coverageDirect,
    loads,
    surfaces: subsEntries, // legacy
    installedByScenario: totalKwp,
  };
};

export const calcSummary = (inputs) => {
  const saffron = calcSaffron(inputs.saffron);
  const algae = calcAlgae(inputs.algae);
  const opex = calcOpex(inputs.opex);
  const futureLots = calcFutureLots(inputs.future_lots || []);
  const newLots = calcNewLots(inputs.new_lots || {});
  const energy = calcEnergy(inputs.energy);

  const coreRevenue = saffron.total + algae.total;
  // PHASE 1 — LEGACY CLEANUP:
  // new_lots is the canonical calculation source. future_lots is retained as a
  // compatibility/legacy layer (still exposed via summary.futureLots for UI
  // panels such as FutureLotsTab) but MUST NOT contribute to the economic
  // aggregates below, otherwise the same lots (lot3, lot4, lot5) would be
  // counted twice in the baseline scenario. See PRD/CHANGE_PLAN Section L.1.
  const grossRevenue = coreRevenue + newLots.total.revenue;
  const totalOpexAnnualBase = opex.annual + newLots.total.opex;

  // Energy savings: se abilitato, riduce OPEX totale
  const energySavings = energy && inputs.energy?.apply_opex_savings ? energy.energy_savings : 0;
  const totalOpexAnnual = Math.max(0, totalOpexAnnualBase - energySavings);
  const totalOpexMonthly = totalOpexAnnual / 12;

  const operatingMargin = grossRevenue - totalOpexAnnual;
  const marginPct = grossRevenue > 0 ? (operatingMargin / grossRevenue) * 100 : 0;
  const marginRatio = grossRevenue > 0 ? operatingMargin / grossRevenue : 0;
  const breakevenMonthlyRevenue = marginRatio > 0 ? totalOpexMonthly / marginRatio : null;

  const energyCapex = energy && inputs.energy?.include_in_capex ? energy.energy_capex : 0;
  const totalCapex = (inputs.capex?.initial_investment || 0) + newLots.total.capex + energyCapex;
  const paybackYears = operatingMargin > 0 ? totalCapex / operatingMargin : null;
  const roi5y = totalCapex > 0 ? ((operatingMargin * 5) / totalCapex) * 100 : 0;

  return {
    saffron, algae, opex, futureLots, newLots, energy,
    coreRevenue,
    grossRevenue,
    totalOpexAnnual,
    totalOpexAnnualBase,
    energySavings,
    totalOpexMonthly,
    operatingMargin,
    marginPct,
    breakevenMonthlyRevenue,
    energyCapex,
    totalCapex,
    paybackYears,
    roi5y,
  };
};

export const calc5YearProjection = (inputs) => {
  const { reinvestment_pct, expansion_capex_per_pct, opex_inflation_pct, revenue_inflation_pct } = inputs.projection;
  const base = calcSummary(inputs);
  const installYear = inputs.energy?.install_year || 1;
  const energyCapexTotal = inputs.energy?.include_in_capex ? (base.energy?.energy_capex || 0) : 0;
  const energySavingsAnnual = inputs.energy?.apply_opex_savings ? (base.energy?.energy_savings || 0) : 0;

  // Compute base excluding new_lots contribution — new lots are added by year
  const newLotsItems = base.newLots?.items || [];
  const newLotsRevenue = base.newLots?.total?.revenue || 0;
  const newLotsOpex = base.newLots?.total?.opex || 0;

  const baseRevenueNoEnergyNoNewLots = base.grossRevenue - newLotsRevenue;
  const baseOpexNoEnergyNoNewLots = base.totalOpexAnnualBase - newLotsOpex;

  let capacity = 1;
  let cumulativeCash = 0;
  const rows = [];

  for (let year = 1; year <= 5; year++) {
    const revInflation = Math.pow(1 + revenue_inflation_pct / 100, year - 1);
    const opexInflation = Math.pow(1 + opex_inflation_pct / 100, year - 1);
    let revenue = baseRevenueNoEnergyNoNewLots * capacity * revInflation;
    let opex = baseOpexNoEnergyNoNewLots * capacity * opexInflation;
    let energy_saving_year = 0;
    let capex_year = 0;
    let new_lots_revenue_year = 0;
    let new_lots_opex_year = 0;
    let new_lots_capex_year = 0;

    // Apply new lots based on activation_year
    newLotsItems.forEach((x) => {
      if (!x.lot.enabled) return;
      const actYear = x.lot.activation_year || 1;
      if (year >= actYear) {
        new_lots_revenue_year += x.calc.revenue * revInflation;
        new_lots_opex_year += x.calc.opex * opexInflation;
      }
      if (year === actYear) {
        new_lots_capex_year += x.calc.capex;
      }
    });

    revenue += new_lots_revenue_year;
    opex += new_lots_opex_year;

    if (year >= installYear) {
      energy_saving_year = energySavingsAnnual * opexInflation;
      opex = Math.max(0, opex - energy_saving_year);
    }
    if (year === installYear) {
      capex_year += energyCapexTotal;
    }
    capex_year += new_lots_capex_year;

    const margin = revenue - opex;
    const reinvested = margin > 0 ? (margin * reinvestment_pct) / 100 : 0;
    const netCash = margin - reinvested - capex_year;
    cumulativeCash += netCash;

    rows.push({
      year,
      capacity,
      revenue,
      opex,
      energy_saving_year,
      capex_year,
      new_lots_revenue_year,
      new_lots_capex_year,
      margin,
      reinvested,
      netCash,
      cumulativeCash,
      ebitdaMargin: revenue > 0 ? (margin / revenue) * 100 : 0,
    });

    if (reinvested > 0 && expansion_capex_per_pct > 0) {
      const growthPct = reinvested / expansion_capex_per_pct;
      capacity *= 1 + growthPct / 100;
    }
  }
  return rows;
};

// --- Payback dynamic (uses 5Y projection) -----------------
export const calcPaybackDynamic = (inputs) => {
  const capex = inputs.capex?.initial_investment || 0;
  if (capex <= 0) return { payback_years: 0, reached: true, cumulative_at_5y: 0 };
  const rows = calc5YearProjection(inputs);
  // Look for year where cumulative net cash flow (before considering CAPEX)
  // exceeds the initial investment.
  let cumulative = 0;
  let prevCumulative = 0;
  for (const r of rows) {
    // Use margin (not netCash) as cashflow for payback — reinvested profit still returns to firm
    cumulative += r.margin;
    if (cumulative >= capex) {
      // Linear interpolation within the year
      const needed = capex - prevCumulative;
      const fraction = r.margin > 0 ? needed / r.margin : 0;
      return {
        payback_years: r.year - 1 + fraction,
        reached: true,
        cumulative_at_5y: rows[rows.length - 1] ? cumulative : 0,
      };
    }
    prevCumulative = cumulative;
  }
  const finalCumulative = rows.reduce((s, r) => s + r.margin, 0);
  return { payback_years: null, reached: false, cumulative_at_5y: finalCumulative };
};

// --- IRR (bisection on NPV) --------------------------------
const npv = (rate, cashflows) => {
  let sum = 0;
  cashflows.forEach((cf, i) => { sum += cf / Math.pow(1 + rate, i); });
  return sum;
};

export const calcIRR = (inputs) => {
  const capex = inputs.capex?.initial_investment || 0;
  if (capex <= 0) return null;
  const rows = calc5YearProjection(inputs);
  const cashflows = [-capex, ...rows.map((r) => r.margin)];
  // Bisection between -0.99 and 5
  let low = -0.99;
  let high = 5.0;
  const npvHigh = npv(high, cashflows);
  const npvLow = npv(low, cashflows);
  // Se totalmente negativo o positivo: IRR fuori range
  if (npvLow * npvHigh > 0) {
    // Nessun cambio di segno → IRR non definito
    return null;
  }
  for (let i = 0; i < 200; i++) {
    const mid = (low + high) / 2;
    const nm = npv(mid, cashflows);
    if (Math.abs(nm) < 1) return mid; // €1 tolerance
    const nl = npv(low, cashflows);
    if (nl * nm < 0) high = mid; else low = mid;
  }
  return (low + high) / 2;
};

// --- Optimizer: grid config surface × PBR volume -----------
// Compute derived production, revenue, OPEX, EBITDA, CAPEX, ROI, payback,
// break-even for every combination of (surface_m2, pbr_volume_m3).

export const runOptimizer = (baseInputs) => {
  const combos = [];
  const s = baseInputs.saffron;
  const a = baseInputs.algae;
  const opexAnnualBase = calcOpex(baseInputs.opex).annual;

  // Baseline reference for CAPEX delta
  const baselineCapexUnit = s.surface_m2 * CAPEX_UNIT.saffron_per_m2 + a.pbr_volume_m3 * CAPEX_UNIT.algae_per_m3;

  for (const surface of OPTIMIZER_SURFACES) {
    for (const volume of OPTIMIZER_VOLUMES) {
      // Scale production linearly with surface & volume vs current inputs
      const surfaceScale = surface / s.surface_m2;
      const volumeScale = volume / a.pbr_volume_m3;

      const saffronCfg = { ...s, surface_m2: surface, kg_year: s.kg_year * surfaceScale };
      const algaeCfg = {
        pbr_volume_m3: volume,
        productivity_g_l_d: a.productivity_g_l_d,
        chlorella: { ...a.chlorella, kg_year: a.chlorella.kg_year * volumeScale },
        spirulina: { ...a.spirulina, kg_year: a.spirulina.kg_year * volumeScale },
        scenedesmus: { ...a.scenedesmus, kg_year: a.scenedesmus.kg_year * volumeScale },
      };

      const saffronCalc = calcSaffron(saffronCfg);
      const algaeCalc = calcAlgae(algaeCfg);
      // PHASE 1 — LEGACY CLEANUP: Optimizer must consume the canonical
      // new_lots source, not the legacy future_lots list. This preserves
      // consistency with calcSummary (which now excludes futureLots from
      // grossRevenue/OPEX/CAPEX).
      const newLotsAgg = calcNewLots(baseInputs.new_lots || {});

      // OPEX scala parzialmente: 60% variabile con capacità media (surface+volume scale), 40% fisso
      const capScale = (surfaceScale + volumeScale) / 2;
      const opexScaled = opexAnnualBase * (0.4 + 0.6 * capScale) + newLotsAgg.total.opex;

      const revenue = saffronCalc.total + algaeCalc.total + newLotsAgg.total.revenue;
      const ebitda = revenue - opexScaled;
      const ebitdaMargin = revenue > 0 ? (ebitda / revenue) * 100 : 0;

      // CAPEX totale = struttura Lotti 1-2 (unit cost) + capex nuovi lotti attivi
      const capexUnit = surface * CAPEX_UNIT.saffron_per_m2 + volume * CAPEX_UNIT.algae_per_m3;
      const capexTotal = capexUnit + newLotsAgg.total.capex;
      const capexDelta = capexUnit - baselineCapexUnit;

      const payback = ebitda > 0 ? capexTotal / ebitda : null;
      const roi5y = capexTotal > 0 ? ((ebitda * 5) / capexTotal) * 100 : 0;
      const marginRatio = revenue > 0 ? ebitda / revenue : 0;
      const breakevenMonthly = marginRatio > 0 ? opexScaled / 12 / marginRatio : null;

      combos.push({
        key: `s${surface}_v${volume}`,
        surface_m2: surface,
        pbr_volume_m3: volume,
        saffron_kg: saffronCfg.kg_year,
        algae_kg_total: algaeCfg.chlorella.kg_year + algaeCfg.spirulina.kg_year + algaeCfg.scenedesmus.kg_year,
        revenue,
        opex: opexScaled,
        ebitda,
        ebitdaMargin,
        capex_total: capexTotal,
        capex_delta: capexDelta,
        roi5y,
        payback,
        breakeven_monthly: breakevenMonthly,
      });
    }
  }

  // Highlight: minimum config with EBITDA >= 0.
  // "Minimum" = smallest capex_total among the positive ones.
  const positive = combos.filter((c) => c.ebitda >= 0);
  const optimal = positive.length > 0
    ? positive.reduce((best, c) => (c.capex_total < best.capex_total ? c : best), positive[0])
    : null;

  return { combos, optimalKey: optimal ? optimal.key : null };
};
